import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { ChoiceButtonGrid } from "../../components/CustomComponents/Buttons/ChoiceButton/choiceButton";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import { isGoFatafatEnabled } from "../../utils/companyCapabilities";

const BREAK_PRESETS = [
  { id: 15, title: "15 min", description: "Quick pause", default: true },
  { id: 30, title: "30 min", description: "Short break", default: false },
  { id: 45, title: "45 min", description: "Busy period", default: false },
  { id: 60, title: "60 min", description: "One hour", default: false },
  { id: 90, title: "90 min", description: "Extended", default: false },
  { id: 120, title: "2 hours", description: "Max busy", default: false },
];

const ToggleRow = ({
  label,
  description,
  checked,
  onChange,
  disabled,
}) => (
  <div className="list-group-item d-flex align-items-center justify-content-between">
    <div className="flex-fill pe-3">
      <div className="fw-semibold">{label}</div>
      {description ? (
        <div className="text-muted small mt-1">{description}</div>
      ) : null}
    </div>
    <div className="form-check form-switch m-0">
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      />
    </div>
  </div>
);

const mapCategoriesFromApi = (categories = []) => {
  const options = categories
    .filter((category) => (category?.isActive ?? category?.IsActive) === true)
    .map((category) => ({
      value: category.id ?? category.ID,
      label: category.name ?? category.Name,
    }));
  const selectedIds = categories
    .filter((category) => (category?.isSelected ?? category?.IsSelected) === true)
    .map((category) => category.id ?? category.ID);
  return { options, selectedIds };
};

const applySettingsState = (data, setters) => {
  if (!data) return;
  setters.setIsVisible(data?.isVisible ?? data?.IsVisible ?? true);
  setters.setIsOnBreak(data?.isOnBreak ?? data?.IsOnBreak ?? false);
  setters.setBreakUntil(data?.breakUntil ?? data?.BreakUntil ?? null);
  setters.setRemainingBreakMinutes(
    data?.remainingBreakMinutes ?? data?.RemainingBreakMinutes ?? 0
  );
  setters.setIsAcceptingOrders(
    data?.isAcceptingOrders ?? data?.IsAcceptingOrders ?? true
  );
  const mins = data?.breakMinutes ?? data?.BreakMinutes;
  if (mins) {
    const preset = BREAK_PRESETS.find((p) => p.id === mins);
    setters.setSelectedBreakLabel(preset?.title ?? `${mins} min`);
  }
};

const GoFatafatSettings = () => {
  const themeColor = useThemeColor();
  const profileData = useSelector((state) => state.auth.profileData);
  const marketplaceEnabled = isGoFatafatEnabled(profileData);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [breakBusy, setBreakBusy] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakUntil, setBreakUntil] = useState(null);
  const [remainingBreakMinutes, setRemainingBreakMinutes] = useState(0);
  const [isAcceptingOrders, setIsAcceptingOrders] = useState(true);
  const [selectedBreakLabel, setSelectedBreakLabel] = useState("15 min");
  const [restaurantCategoryOptions, setRestaurantCategoryOptions] = useState([]);
  const [selectedRestaurantCategoryIds, setSelectedRestaurantCategoryIds] = useState([]);
  const [maxRestaurantCategorySelection, setMaxRestaurantCategorySelection] = useState(3);

  // Delivery/Collection are always on for Go Fatafat (not restaurant-configurable).
  const deliveryEnabled = true;
  const collectionEnabled = true;

  const stateSetters = {
    setIsVisible,
    setIsOnBreak,
    setBreakUntil,
    setRemainingBreakMinutes,
    setIsAcceptingOrders,
    setSelectedBreakLabel,
  };

  const fetchRestaurantCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_RESTAURANT_CATEGORIES);
      if (response?.data?.status) {
        const payload = response.data?.data || {};
        const categories = payload?.categories || [];
        const maxSelection = payload?.maxSelection || 3;
        const { options, selectedIds } = mapCategoriesFromApi(categories);
        setMaxRestaurantCategorySelection(maxSelection);
        setRestaurantCategoryOptions(options);
        setSelectedRestaurantCategoryIds(selectedIds);
      } else {
        showErrorToast(
          response?.data?.Message ||
            response?.data?.message ||
            "Failed to load Go Fatafat categories"
        );
      }
    } catch (error) {
      showErrorToast(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Failed to load Go Fatafat categories"
      );
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_GOFATAFAT_SETTINGS);
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status === false || response.data?.Status === false) {
        showErrorToast(
          response.data?.message ||
            response.data?.Message ||
            "Failed to load Go Fatafat settings"
        );
        return;
      }
      applySettingsState(data, stateSetters);
      await fetchRestaurantCategories();
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          "Failed to load Go Fatafat settings"
      );
    } finally {
      setLoading(false);
    }
  }, [fetchRestaurantCategories]);

  useEffect(() => {
    if (marketplaceEnabled) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [marketplaceEnabled, fetchSettings]);

  const handleSave = async () => {
    if (selectedRestaurantCategoryIds.length > maxRestaurantCategorySelection) {
      showErrorToast(
        `You can select up to ${maxRestaurantCategorySelection} categories only`
      );
      return;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.put(APIs.PUT_GOFATAFAT_SETTINGS, {
        IsVisible: isVisible,
        DeliveryEnabled: deliveryEnabled,
        CollectionEnabled: collectionEnabled,
      });
      if (response.data?.status === false || response.data?.Status === false) {
        showErrorToast(
          response.data?.message ||
            response.data?.Message ||
            "Failed to save Go Fatafat settings"
        );
        return;
      }

      const categoryResponse = await axiosInstance.put(
        APIs.PUT_COMPANY_RESTAURANT_CATEGORIES,
        { categoryIds: selectedRestaurantCategoryIds },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!(categoryResponse?.data?.status === true)) {
        showErrorToast(
          categoryResponse?.data?.Message ||
            categoryResponse?.data?.message ||
            "Failed to update Go Fatafat categories"
        );
        return;
      }

      const data = response.data?.data ?? response.data?.Data;
      applySettingsState(data, stateSetters);

      const refreshed = categoryResponse?.data?.data || {};
      setMaxRestaurantCategorySelection(
        refreshed?.maxSelection || maxRestaurantCategorySelection
      );
      const { options, selectedIds } = mapCategoriesFromApi(refreshed?.categories || []);
      setRestaurantCategoryOptions(options);
      setSelectedRestaurantCategoryIds(selectedIds);

      showSuccessToast("Go Fatafat settings updated");
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          "Failed to save Go Fatafat settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const resolveBreakMinutes = (label) => {
    const preset = BREAK_PRESETS.find((p) => p.title === label);
    return preset?.id ?? 15;
  };

  const handleStartBreak = async () => {
    try {
      setBreakBusy(true);
      const minutes = resolveBreakMinutes(selectedBreakLabel);
      const response = await axiosInstance.post(APIs.POST_GOFATAFAT_BREAK, {
        Minutes: minutes,
      });
      if (response.data?.status === false || response.data?.Status === false) {
        showErrorToast(
          response.data?.message ||
            response.data?.Message ||
            "Failed to start break"
        );
        return;
      }
      const data = response.data?.data ?? response.data?.Data;
      applySettingsState(data, stateSetters);
      showSuccessToast(
        response.data?.message ||
          response.data?.Message ||
          `Break started for ${minutes} minutes`
      );
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          "Failed to start break"
      );
    } finally {
      setBreakBusy(false);
    }
  };

  const handleEndBreak = async () => {
    try {
      setBreakBusy(true);
      const response = await axiosInstance.delete(APIs.DELETE_GOFATAFAT_BREAK);
      if (response.data?.status === false || response.data?.Status === false) {
        showErrorToast(
          response.data?.message ||
            response.data?.Message ||
            "Failed to end break"
        );
        return;
      }
      const data = response.data?.data ?? response.data?.Data;
      applySettingsState(data, stateSetters);
      showSuccessToast(
        response.data?.message ||
          response.data?.Message ||
          "Break ended — accepting orders again"
      );
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          "Failed to end break"
      );
    } finally {
      setBreakBusy(false);
    }
  };

  if (!marketplaceEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return <Preloader />;
  }

  const breakUntilDisplay = breakUntil
    ? new Date(breakUntil).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h3 className="mb-1" style={{ color: themeColor }}>
            Go Fatafat Settings
          </h3>
          <p className="text-muted mb-0">
            Control how your restaurant appears on the Go Fatafat marketplace.
          </p>
        </div>
        <span className="badge" style={{ backgroundColor: themeColor }}>
          {isOnBreak
            ? "On short break"
            : isAcceptingOrders
              ? "Accepting orders"
              : "Not accepting"}
        </span>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-white">
          <div className="fw-semibold">Short break</div>
          <div className="text-muted small">
            Pause new marketplace orders. The restaurant stays on the Go Fatafat list with an
            “Opens at …” overlay until the break ends. It goes back to accepting orders when
            the timer ends (or when you resume early). Permanent “Show on Go Fatafat” is not changed.
          </div>
        </div>
        {isOnBreak ? (
          <div className="card-body">
            <div className="alert alert-warning mb-3">
              On break
              {breakUntilDisplay ? ` until about ${breakUntilDisplay}` : ""}.
              {remainingBreakMinutes > 0
                ? ` ~${remainingBreakMinutes} min remaining.`
                : ""}{" "}
              New customer orders are blocked until you resume or the timer ends.
            </div>
            <button
              type="button"
              className="btn text-white"
              style={{ backgroundColor: themeColor }}
              onClick={handleEndBreak}
              disabled={breakBusy}
            >
              {breakBusy ? "Resuming…" : "Resume orders now"}
            </button>
          </div>
        ) : (
          <div className="card-body">
            <ChoiceButtonGrid
              buttons={BREAK_PRESETS}
              activeButton={selectedBreakLabel}
              handleClick={setSelectedBreakLabel}
            />
            <div className="mt-3">
              <button
                type="button"
                className="btn text-white"
                style={{ backgroundColor: themeColor }}
                onClick={handleStartBreak}
                disabled={breakBusy || !isVisible}
              >
                {breakBusy ? "Starting…" : "Start short break"}
              </button>
            </div>
            {!isVisible ? (
              <div className="text-muted small mt-2">
                Turn on “Show on Go Fatafat” before starting a break.
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-white">
          <div className="fw-semibold">
            Go Fatafat categories (max {maxRestaurantCategorySelection})
          </div>
          <div className="text-muted small">
            These categories are shown on Go Fatafat browse and search filters. Pick up to{" "}
            {maxRestaurantCategorySelection}.
          </div>
        </div>
        <div className="card-body">
          <Select
            options={restaurantCategoryOptions}
            value={restaurantCategoryOptions.filter((option) =>
              selectedRestaurantCategoryIds.includes(option.value)
            )}
            isMulti
            closeMenuOnSelect={false}
            onChange={(options) => {
              const ids = (options || []).map((option) => option.value);
              if (ids.length > maxRestaurantCategorySelection) {
                showErrorToast(
                  `You can select up to ${maxRestaurantCategorySelection} categories only`
                );
                return;
              }
              setSelectedRestaurantCategoryIds(ids);
            }}
            placeholder="Select Go Fatafat categories"
            className="react-select-container"
            classNamePrefix="react-select"
            isDisabled={saving}
          />
          <div className="text-muted mt-2 small">
            Selected {selectedRestaurantCategoryIds.length} of {maxRestaurantCategorySelection}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="list-group list-group-flush">
          <ToggleRow
            label="Show on Go Fatafat"
            description="When off, your restaurant is hidden from customer browse and search."
            checked={isVisible}
            onChange={setIsVisible}
            disabled={saving}
          />
        </div>
        <div className="card-footer bg-white d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={fetchSettings}
            disabled={saving || breakBusy}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn text-white"
            style={{ backgroundColor: themeColor }}
            onClick={handleSave}
            disabled={saving || breakBusy}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoFatafatSettings;
